// ═══════════════════════════════════════
// RENDERER — ABCJS render & audio
// ═══════════════════════════════════════

import { ORNAMENT_TOTAL_DURATION, DRONE_FREQS_HP, DRONE_FREQS_CMIN } from './config.js';
import { getAbcForRenderWithMap } from './editor.js';
import { startDrones, stopDrones } from './drone.js';
import { trackEvent } from './analytics.js';
import { currentSlug } from './state.js';
import { processChordLyrics } from './chord-lyrics.js';
import { setRenderSourceMap, onRenderClick, syncRenderFromCursor } from './cursor-sync.js';



export const SOUND_FONT_URL = '/soundfonts/';
const INSTRUMENTS = window.INSTRUMENTS;
export let synthControl = null;
export let visualObj    = null;
// sequenceCallback du dernier rendu — réutilisé par l'export MP3
export let currentSequenceCallback = null;
let _currentDroneFreqs = DRONE_FREQS_HP;

function setSynthControl(v) { synthControl = v; }
function setVisualObj(v)    { visualObj = v; }

const DEBUG = window.SCOROU_DEBUG === true;

function debugPanel() {
    let el = document.getElementById('scorou-debug-panel');
    if (!el) {
        el = document.createElement('div');
        el.id = 'scorou-debug-panel';
        el.style.cssText = 'position:fixed;bottom:0;left:0;right:0;max-height:40vh;overflow:auto;'
            + 'background:#000;color:#0f0;font:11px monospace;padding:8px;z-index:99999;white-space:pre-wrap;';
        document.body.appendChild(el);
    }
    return el;
}

function debugLog(msg) {
    if (!DEBUG) return;
    console.log('[SCOROU DEBUG]', msg);
    debugPanel().textContent += msg + '\n';
}


export function isDroneChecked() {
    return document.getElementById('drone-checkbox')?.checked ?? false;
}

function isBretonNotation(abcString) {
    return /K:\s*Cm(in(or)?)?(?=\s|$|])/i.test(abcString);
}

export function isPlaying() {
    return document.querySelector('#audio .abcjs-midi-start')
        ?.classList.contains('abcjs-pushed') ?? false;
}

function clickListener(abcElem) {
    // Place le curseur de l'éditeur sur l'élément cliqué (note, silence, barre…)
    onRenderClick(abcElem);

    if (!abcElem.midiPitches || !visualObj) return;

    // Remapping pitch pour les percussions (canal 10 = percussion GM)
    const pitches = abcElem.midiPitches.map(p => {
        /*if (p.instrument === 128) {
            return Object.assign({}, p, { pitch: 40 });
        }*/
        return p;
    });

    console.log(pitches);

    ABCJS.synth.playEvent(
        pitches,
        abcElem.midiGraceNotePitches,
        visualObj.millisecondsPerMeasure(),
        SOUND_FONT_URL
    ).catch(e => console.log('Erreur clic note:', e));
}


// Construit le Set des premières lignes de program_abc des instruments bagpipe.
// Ex : new Set(['%%MIDI program 109'])
function _getBagpipePrograms() {
    const lines = new Set();
    for (const inst of INSTRUMENTS) {
        if (inst.name !== 'bagpipe') continue;
        const first = inst.program.split('\n').map(l => l.trim()).find(l => l);
        if (first) lines.add(first);
    }
    return lines;
}

const _bagpipePrograms = _getBagpipePrograms();


function getBagpipeTrackIndexes(abcString) {
    const indexes  = new Set();
    const lines    = abcString.split('\n');
    let voiceIndex = -1;
    const hasVoices = /^V:/m.test(abcString);

    for (const line of lines) {
        if (/^V:/.test(line)) {
            voiceIndex++;
        } else if (_bagpipePrograms.has(line.trim())) {
            if (hasVoices && voiceIndex >= 0) {
                indexes.add(voiceIndex);
            } else if (!hasVoices) {
                indexes.add(0);
            }
        }
    }
    return indexes;
}

// Ligne insérée par injectBretonTranspose() — connue de cursor-sync.js
// pour réaligner les lignes de l'ABC rendu sur celles de l'éditeur.
const BRETON_TRANSPOSE_LINE = '%%MIDI transpose 11';

function injectBretonTranspose(abcString) {
    if (!isBretonNotation(abcString)) return abcString;

    const lines = abcString.split('\n');
    const result = [];
    let pendingBagpipe = false;

    for (const line of lines) {
        if (/^%%MIDI program\s+(109|68)/.test(line)) {
            pendingBagpipe = true;
            result.push(line);
        } else if (pendingBagpipe && /^%%MIDI transpose/.test(line)) {
            // remplacer un éventuel transpose existant
            result.push(BRETON_TRANSPOSE_LINE);
            pendingBagpipe = false;
        } else {
            if (pendingBagpipe) {
                result.push(BRETON_TRANSPOSE_LINE);
                pendingBagpipe = false;
            }
            result.push(line);
        }
    }
    // cas où %%MIDI program 109 est la dernière ligne
    if (pendingBagpipe) result.push(BRETON_TRANSPOSE_LINE);

    return result.join('\n');
}

/*
function stripPercussionTies(abcString) {
    const lines = abcString.split('\n');

    // ── Passe 1 : identifier les numéros de voix percussion ──────────────────
    // On remonte depuis chaque %%MIDI program 128 pour trouver le V: précédent.
    const percVoices = new Set();
    for (let i = 0; i < lines.length; i++) {
        if (/^%%MIDI\s+program\s+128\b/.test(lines[i])) {
            for (let j = i - 1; j >= 0; j--) {
                const m = lines[j].match(/^V:(\S+)/);
                if (m) { percVoices.add(m[1]); break; }
            }
        }
    }

    if (percVoices.size === 0) return abcString;

    // ── Passe 2 : nettoyer les lignes de notes des voix percussion ────────────
    const result = [];
    let currentVoice = null;

    for (const line of lines) {
        const vm = line.match(/^V:(\S+)/);
        if (vm) currentVoice = vm[1];

        const isPercussion = percVoices.has(currentVoice);
        result.push(isPercussion && !/^[A-Z%]/.test(line)
            ? line.replace(/-/g, '')
            : line
        );
    }
    return result.join('\n');
}
*/

// ─────────────────────────────────────────────────────────────
// Détection des décorations de trémolo dans le texte ABC brut.
// Retourne le nombre de barres (1, 2 ou 3) si la note à startChar
// est précédée de !/!, !//! ou !///!, sinon 0.
// ─────────────────────────────────────────────────────────────
function getTremoloSlashes(abc, prevEndChar, startChar) {
    if (startChar == null) return 0;
    // On cherche !//! uniquement dans l'intervalle entre la note précédente
    // et la note courante — ce qui correspond exactement à la décoration ABC.
    const from = prevEndChar != null ? prevEndChar : Math.max(0, startChar - 12);
    const zone = abc.substring(from, startChar);
    const m = zone.match(/!(\/+)!/);
    if (!m) return 0;
    return m[1].length;
}

// ─────────────────────────────────────────────────────────────
// Subdivision d'une note en N répétitions rapides.
// note  : objet de la track abcjs { start, end, pitch, volume, instrument, ... }
// n     : nombre de subdivisions
// Retourne un tableau de n notes qui remplace la note originale.
// ─────────────────────────────────────────────────────────────
function subdivideTremolo(note, n) {
    const duration = (note.end - note.start) / n;
    const result = [];
    for (let i = 0; i < n; i++) {
        result.push(Object.assign({}, note, {
            start: note.start + i * duration,
            end:   note.start + (i + 1) * duration,
            // légère variation de volume pour imiter le grain du buzz
            volume: Math.round(note.volume * (i % 2 === 0 ? 1.0 : 0.85)),
        }));
    }
    return result;
}

// ─────────────────────────────────────────────────────────────
// sequenceCallback — à passer dans midiBuffer.init() ET setTune()
// abc : le texte ABC courant (capturé via closure dans render())
// ─────────────────────────────────────────────────────────────
function makeSequenceCallback(abc) {
    return function sequenceCallback(tracks) {

console.log(tracks);
tracks.forEach((track) => {
    for (let i = 0; i < track.length; i++) {
         const note = track[i];
        console.log(abc.substring(note.startChar, note.endChar));
    }
});


        // ── Logique cornemuse existante (grace notes + prolongation) ──
        // (laisser ici le code cornemuse quand il sera réactivé)

        // ── Trémolo + remapping percussion ──
        tracks.forEach((track) => {
            const newTrack = [];
            for (let i = 0; i < track.length; i++) {
                const note = track[i];

                if (note.instrument === 'percussion') {
                    // Remapping pitch
                    //note.pitch = 40;

                    // Ignorer grace notes et rests
                    if (note.style === 'grace' || note.pitch == null) {
                        newTrack.push(note);
                        continue;
                    }

                    // Trémolo
                    const slashes = getTremoloSlashes(abc, note.startChar, note.endChar);
                    //const slashes = getTremoloSlashes(abc, i > 0 ? track[i - 1].endChar : null, note.startChar);
                    if (slashes === 0) {
                        newTrack.push(note);
                        continue;
                    }
                    const n = Math.pow(2, slashes);
                    subdivideTremolo(note, n).forEach(s => newTrack.push(s));
                } else {
                    newTrack.push(note);
                }
            }
            track.length = 0;
            newTrack.forEach(n => track.push(n));

        });

        console.log(tracks);
        return tracks;
    };
}


export function render() {
    const { abc: preAbc, lineMap: preLineMap } = getAbcForRenderWithMap();
    const abc = injectBretonTranspose(preAbc);
    //const abcMidi = stripPercussionTies(abc);
    const sequenceCallback = makeSequenceCallback(abc);
    currentSequenceCallback = sequenceCallback;
    const bagpipeIndexes = getBagpipeTrackIndexes(abc);
    const hasBagpipe = bagpipeIndexes.size > 0;

    //_currentDroneFreqs = isBretonNotation(abc) ? DRONE_FREQS_CMIN : DRONE_FREQS_HP;


    function _sequenceCallback(tracks) {
        console.log(tracks);
        return tracks;
    /*
    console.log('sequenceCallback appelée, nb tracks:', tracks.length);
    console.log('bagpipeIndexes:', [...bagpipeIndexes]);

    tracks.forEach((track, trackIndex) => {
        const isBagpipe = bagpipeIndexes.has(trackIndex);
        console.log(`Track ${trackIndex} — isBagpipe: ${isBagpipe} — ${track.length} événements`);
        track.forEach((note, i) => {
            console.log(note);
            console.log(
                `  [${i}]`,
                `start=${note.start.toFixed(4)}`,
                `end=${note.end.toFixed(4)}`,
                `pitch=${note.pitch ?? 'N/A'}`,
                `style=${note.style ?? '-'}`,
                i > 0 ? `gap=${(note.start - track[i-1].end).toFixed(4)}` : ''
            );
        });
    });


        tracks.forEach((track, trackIndex) => {
            const isBagpipe = bagpipeIndexes.has(trackIndex);
            let i = 0;
            while (i < track.length) {
                if (track[i].style !== 'grace') { i++; continue; }
                let graceEnd = i;
                while (graceEnd + 1 < track.length && track[graceEnd + 1].style === 'grace') {
                    graceEnd++;
                }
                const graceCount = graceEnd - i + 1;
                const graceDur   = ORNAMENT_TOTAL_DURATION / graceCount;
                const graceStart = track[i].start;
                for (let k = 0; k < graceCount; k++) {
                    track[i + k].start = graceStart + (k * graceDur);
                    track[i + k].end   = graceStart + ((k + 1) * graceDur);
                }
                const mainNote  = track[graceEnd + 1];
                const noteAfter = track[graceEnd + 2];
                if (mainNote) {
                    mainNote.start = graceStart + ORNAMENT_TOTAL_DURATION;
                    if (noteAfter) mainNote.end = noteAfter.start;
                }
                if (isBagpipe && i > 0 && track[i - 1].style !== 'grace') {
                    track[i - 1].end = graceStart;
                }
                i = graceEnd + 1;
            }
        });
        return tracks;
        */
    }

    console.log(hasBagpipe);
    console.log(abc);

    const result = ABCJS.renderAbc('notation', abc, {
        stretchLastLine: true,
        responsive: 'resize',
        addClasses: true,
        clickListener,
        bagpipes: hasBagpipe
    });
    setVisualObj(result[0]);

    // Synchro curseur éditeur ↔ rendu : correspondance offsets → voix/ligne,
    // puis re-surlignage de l'élément sous le curseur (le rendu vient d'être recréé)
    setRenderSourceMap(preAbc, preLineMap, abc, visualObj, BRETON_TRANSPOSE_LINE);
    syncRenderFromCursor();

    // Post-traitement accords dans W:
    processChordLyrics(document.getElementById('notation'));


    /*console.log(abcMidi);
    const resultMidi = ABCJS.renderAbc('*', abcMidi, {
        stretchLastLine: true,
        responsive: 'resize',
        addClasses: true,
        clickListener,
        bagpipes: hasBagpipe
    });
    setVisualObjMidi(resultMidi[0]);*/

    console.log(visualObj);



    let supportsAudioResult;
    try {
        supportsAudioResult = ABCJS.synth.supportsAudio();
        debugLog('supportsAudio() = ' + supportsAudioResult);
    } catch (e) {
        debugLog('supportsAudio() a levé une exception: ' + (e && e.message ? e.message : e));
        supportsAudioResult = false;
    }

    if (supportsAudioResult) {
        debugLog('supportsAudio OK — init du synth...');
        const midiBuffer = new ABCJS.synth.CreateSynth();
        midiBuffer.init({
            visualObj: visualObj,
            options: { soundFontUrl: SOUND_FONT_URL, sequenceCallback }
        }).then(() => {
            debugLog('midiBuffer.init() résolu — construction du lecteur...');
            const CursorControl = function() {
                this.beatSubdivisions = 2;
                this.onStart    = () => {};
                this.onFinished = () => { stopDrones(); };
                this.onBeat     = () => {};
                this.onEvent    = (event) => {
                    if (event.midiPitches && event.midiPitches.length > 0) {
                        document.querySelectorAll('#notation svg g[data-name="note"] path')
                            .forEach(n => n.setAttribute('fill', '#000000'));
                        let firstEl = null;
                        if (event.elements) {
                            event.elements.forEach(voiceElements => {
                                if (voiceElements) {
                                    voiceElements.forEach(el => {
                                        if (el) {
                                            el.querySelectorAll('path')
                                                .forEach(p => p.setAttribute('fill', '#c8a96e'));
                                            if (!firstEl) firstEl = el;
                                        }
                                    });
                                }
                            });
                        }
                        if (firstEl) {
                            const container = document.querySelector('.partition-view-inner');
                            if (container) {
                                const elRect        = firstEl.getBoundingClientRect();
                                const containerRect = container.getBoundingClientRect();
                                const marginTop    = 40;
                                const marginBottom = 150; // anticipe ~1 ligne musicale
                                const aboveView = elRect.top    < containerRect.top    + marginTop;
                                const belowView = elRect.bottom > containerRect.bottom - marginBottom;
                                if (aboveView) {
                                    const delta = elRect.top - containerRect.top - marginTop;
                                    container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
                                } else if (belowView) {
                                    const delta = elRect.bottom - containerRect.bottom + marginBottom;
                                    container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
                                }
                            }
                        }
                    }
                };
            };

            setSynthControl(new ABCJS.synth.SynthController());
            synthControl.load('#audio', new CursorControl(), {
                displayLoop: true, displayRestart: true,
                displayPlay: true, displayProgress: true,
                displayWarp: true
            });

            synthControl.setTune(visualObj, false, {
                sequenceCallback,
                soundFontUrl: SOUND_FONT_URL,
            });

            _bindAudioButtons();
            debugLog('Lecteur construit avec succès.');

            const audioEl     = document.getElementById('audio');
            const containerEl = document.getElementById('audio-container');
            debugLog('audio.children.length = ' + (audioEl ? audioEl.children.length : 'audioEl introuvable'));
            debugLog('audio.innerHTML.length = ' + (audioEl ? audioEl.innerHTML.length : 'n/a'));
            if (containerEl) {
                debugLog('audio-container.className = "' + containerEl.className + '"');
                const cs = getComputedStyle(containerEl);
                debugLog('audio-container CSS: display=' + cs.display + ' visibility=' + cs.visibility
                    + ' opacity=' + cs.opacity + ' height=' + cs.height);
                const rect = containerEl.getBoundingClientRect();
                debugLog('audio-container rect: top=' + rect.top + ' width=' + rect.width + ' height=' + rect.height);
            } else {
                debugLog('audio-container introuvable dans le DOM');
            }

            debugLog('window.innerHeight=' + window.innerHeight
                + ' visualViewport.height=' + (window.visualViewport ? window.visualViewport.height : 'n/a')
                + ' scrollY=' + window.scrollY);

            let node = containerEl;
            let depth = 0;
            while (node && depth < 8) {
                const r  = node.getBoundingClientRect();
                const cs = getComputedStyle(node);
                debugLog('[' + depth + '] ' + (node.id ? '#' + node.id : node.className || node.tagName)
                    + ' | overflow=' + cs.overflow + '/' + cs.overflowY
                    + ' | height=' + cs.height
                    + ' | rect top=' + r.top.toFixed(1) + ' bottom=' + r.bottom.toFixed(1));
                node = node.parentElement;
                depth++;
            }


        }).catch(e => {
            console.log('Erreur audio:', e);
            const msg = 'ERREUR midiBuffer.init(): ' + (e && e.message ? e.message : e);
            debugLog(msg);
            if (DEBUG) alert(msg);
        });
    } else {
        debugLog('supportsAudio() = false — le lecteur ne sera pas construit.');
    }
}

// Bind les boutons du lecteur abcjs pour synchroniser les bourdons.
// .abcjs-midi-start est le bouton play/pause (bascule).
// Quand il a la classe abcjs-pushed, la lecture EST en cours
// → cliquer dessus signifie "mettre en pause".
// Quand il n'a PAS abcjs-pushed, la lecture est arrêtée/en pause
// → cliquer dessus signifie "lancer/reprendre".
let _audioObserver = null;
function _bindAudioButtons() {
    if (_audioObserver) { _audioObserver.disconnect(); _audioObserver = null; }

    const audioEl = document.getElementById('audio');

    const tryBind = () => {
        const startBtn = audioEl.querySelector('.abcjs-midi-start');
        const resetBtn = audioEl.querySelector('.abcjs-midi-reset');
        if (!startBtn) return false;

        startBtn.addEventListener('click', () => {
            // Au moment du clic, abcjs n'a PAS encore basculé la classe pushed.
            // Si pushed → on était en lecture → c'est une pause.
            // Si non pushed → on était en pause/stop → c'est une reprise/lecture.
            if (startBtn.classList.contains('abcjs-pushed')) {
                // Mise en pause
                stopDrones();
            } else {
                // Lecture ou reprise
                if (isDroneChecked()) startDrones(_currentDroneFreqs);
                trackEvent('Editeur', 'play', currentSlug ?? null);
            }
        });

        if (resetBtn) {
            resetBtn.addEventListener('click', () => {
                // Reset : arrête toujours les bourdons
                stopDrones();
            });
        }

        return true;
    };

    if (tryBind()) return;

    _audioObserver = new MutationObserver(() => {
        if (tryBind()) { _audioObserver.disconnect(); _audioObserver = null; }
    });
    _audioObserver.observe(audioEl, { childList: true, subtree: true });
}